import { useCallback, useState } from 'react';
import { useRouter } from 'next/router';
import { tw } from 'twind';
import { Icon, Button } from '@global-ecom/nitro-uds/elements';

import { useFeature } from 'hooks/useFeature';
import { useTranslate } from 'hooks/useTranslations';
import { ActiveCart } from 'types/cart';
import { getIsMobile } from 'utils/media';
import { useModalsOpenStore, usePopupsOpenStore } from 'store';
import { useMiniCart } from 'hooks/useMiniCart';
import { ProductCartItem } from '__generated__/graphql';

import CloseButton from '../../CloseButton';
import Modal from '../../Modal';

import { GWPOld_ProductSelector } from './GWP.old.ProductSelector';
import { GWPOld_SelectedItem } from './GWP.old.SelectedItem';
import { useGWPModal } from './useGWPModal';

export const GWPOld_Modal = ({ cart }: { cart?: ActiveCart }) => {
  const translate = useTranslate();
  const { setModalOpen } = useModalsOpenStore();
  const hideSelectedBonusItems = useFeature('HIDE_CALLOUT_MESSAGE');
  const { openMiniCart } = useMiniCart();
  const { setPopupOpen } = usePopupsOpenStore();
  const router = useRouter();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [showSelectedItems, setShowSelectedItems] = useState<boolean>(false);

  const isCartPage = router.asPath.includes('/cart');

  const openPopupOrMiniCartAfterClosingModal = useCallback(() => {
    if (!cart?.products) return;

    // don't try to open popup or miniCart when user is on Cart page
    if (!isCartPage) {
      if (getIsMobile()) {
        // in mobile show cart-button-popup
        setPopupOpen('CartButtonPopup', true);
      } else {
        // in desktop show mini-cart with the last item added to the cart
        const lastItemInTheCart = cart?.products?.[cart?.products.length - 1];
        openMiniCart(lastItemInTheCart as ProductCartItem, true);
      }
    }
  }, [cart?.products, isCartPage, openMiniCart, setPopupOpen]);

  const onClose = useCallback(() => {
    setModalOpen('GWPModal', false);
    openPopupOrMiniCartAfterClosingModal();
  }, [openPopupOrMiniCartAfterClosingModal, setModalOpen]);

  const giftWithPurchase = cart?.giftWithPurchases?.find(
    promo => !promo.fullfilled
  );

  const {
    addSelectedItemCallback,
    handleUpdatingPromoGiftsCallback,
    removeSelectedItemCallback,
    updateSelectedItemQuantity,
    selectedItems,
    selectedQuantity,
  } = useGWPModal({
    closeModal: () => setModalOpen('GWPModal', false),
    setError,
    setLoading,
    cart,
    giftWithPurchase,
  });

  if (!giftWithPurchase) return null;

  const showTitleBelowCloseButton =
    getIsMobile() &&
    giftWithPurchase?.details &&
    giftWithPurchase.details?.length > 25;

  return (
    <Modal
      onClickOutside={onClose}
      className="max-w-xl overflow-y-auto"
      variant="dark"
      dataTestId="gwp-old-modal-overlay"
    >
      <div
        data-test-id="gwp-old-modal-body"
        className="lg:col-span-5 xl:col-span-6 md:space-y-4 xs:border xs:p-8 p-4 xs:rounded-xl"
      >
        <h1
          className={tw([
            'border-b pb-5 flex desktop:items-end',
            showTitleBelowCloseButton && 'mobile:flex-col-reverse',
            !showTitleBelowCloseButton && 'mobile:items-end',
          ])}
        >
          <span
            className="text-black-100 uppercase text-lg font-bold mobile:pt-2"
            data-test-id="shopping-cart-header"
          >
            {(!hideSelectedBonusItems && giftWithPurchase.details) ||
              translate('bonusItems')}
          </span>
          <CloseButton
            className="ml-auto text-sm sm:text-base md:text-lg lg:text-xl"
            data-test-id="gwp-old-modal-close"
            onClick={onClose}
          />
        </h1>
        <div id="cart-products" className="flex flex-col flex-grow space-y-10">
          {giftWithPurchase.availableProducts.map((cartItem, idx) => {
            if (cartItem.__typename !== 'ProductWithExclusion') return null;
            return (
              <GWPOld_ProductSelector
                key={`${idx}-${cartItem.product.id}`}
                excludedColors={cartItem.excludedColors}
                product={cartItem.product}
                removeSelectedItem={removeSelectedItemCallback}
                selectedItems={selectedItems}
                addSelectedItem={addSelectedItemCallback}
                updateSelectedItemQuantity={updateSelectedItemQuantity}
                maxAllowedQuantity={giftWithPurchase.allowedQuantity}
              />
            );
          })}
        </div>
        {error && (
          <p
            className="flex items-center space-x-3 justify-between text-puma-red border border-puma-red font-bold px-3 py-2 text-sm rounded mb-2 mt-3"
            role="alert"
            data-test-id="selection-error"
          >
            <span>{error}</span>
            <Icon name="exclamation-outline" size="3xl" color="error" />
          </p>
        )}
        <div className="mt-6">
          <div className="border-b flex">
            <button
              className="flex items-center space-x-2"
              onClick={() => setShowSelectedItems(!showSelectedItems)}
              aria-expanded={showSelectedItems}
              aria-controls="selected-free-gifts-container"
              data-test-id="selected-free-gifts-container-btn"
              type="button"
            >
              <span className="font-bold text-sm uppercase ">
                {translate<'itemsSelected'>('itemsSelected', {
                  allowed: giftWithPurchase.allowedQuantity,
                  selected: selectedQuantity,
                })}
              </span>
              {selectedItems.length > 0 && (
                <span
                  className={tw(
                    'inline-block transform  ml-auto',
                    showSelectedItems ? '-rotate-90' : 'rotate-90'
                  )}
                >
                  <Icon name="chevron-right" size="lg" />
                </span>
              )}
            </button>
          </div>
          {showSelectedItems && (
            <div className="flex flex-col space-y-2.5 overflow-y-auto max-h-56">
              {selectedItems.map(item => (
                <GWPOld_SelectedItem
                  item={item}
                  key={`${item.id}-${item.color}-${item?.size?.id}`}
                  removeSelectedItem={removeSelectedItemCallback}
                />
              ))}
            </div>
          )}
        </div>
        <div id="buttonGroup" className="flex flex-col space-y-4 pt-4 md:pt-0">
          <Button
            disabled={
              loading ||
              !selectedItems.length ||
              selectedQuantity > giftWithPurchase.allowedQuantity ||
              selectedItems.some(item => !item.size)
            }
            loading={loading}
            label={translate('addToCart')}
            variant="primary"
            onClick={() =>
              // don't try to open miniCart when user is on Cart page
              handleUpdatingPromoGiftsCallback({ showMiniCart: !isCartPage })
            }
            dataTestId="gwp-old-modal-add-gifts-to-cart"
          />
          <Button
            label={translate('noThanks')}
            variant="secondary"
            onClick={onClose}
            dataTestId="gwp-old-modal-cancel"
          />
        </div>
      </div>
    </Modal>
  );
};
